import { Box, Checkbox, FormControlLabel, MenuItem, Switch, TextField } from "@mui/material"
import type { FC } from "react"
import { Controller, useFormContext } from "react-hook-form"

import MultiSelect from "@/components/MultiSelect/MultiSelect"
import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES, TEXT_AREA_STYLING_PROPS } from "@/constants/forms"
import { ApplicableToEnum, IncludeWithEnum, TransportationModeEnum } from "@/graphql/codegen/graphql"
import MultipleSelectWithSelectAll from "@/screens/ManageProducts/components/Instructions/components/form/MultipleSelectWithSelectAll"
import {
  applicableToLabelMap,
  includeWithLabelMap,
  transportationModeLabelMap,
} from "@/screens/ManageProducts/components/Instructions/utils/instructionDataMap"

const includedWithOptions = Object.values(IncludeWithEnum).map((value) => ({
  value: value.toString(),
  label: includeWithLabelMap.get(value) ?? value,
}))

const applicableToOptions = Object.values(ApplicableToEnum).map((value) => ({
  value: value.toString(),
  label: applicableToLabelMap.get(value) ?? value,
}))

const transportationModeOptions = Object.values(TransportationModeEnum).map((value) => ({
  value: value.toString(),
  label: transportationModeLabelMap.get(value) ?? value,
}))

const InstructionForm: FC = () => {
  const { control } = useFormContext()

  return (
    <form>
      <Controller
        name='name'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box data-testid='instructions-form-name'>
            <label htmlFor='name' className={FIELD_LABEL_CLASSES}>
              Instruction Name<span className='text-red-600'>*</span>
            </label>
            <TextField {...field} id='name' placeholder='Input Name' error={!!error} fullWidth variant='outlined' />
            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </Box>
        )}
      />

      <Controller
        name='description'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box className='mt-6' data-testid='instructions-form-description'>
            <label htmlFor='description' className={FIELD_LABEL_CLASSES}>
              Instruction Text<span className='text-red-600'>*</span>
            </label>
            <TextField
              {...field}
              {...TEXT_AREA_STYLING_PROPS}
              name={field.name}
              placeholder='Add Text...'
              rows={2}
              error={!!error}
            />
            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </Box>
        )}
      />

      <Box className='my-4'>
        <Controller
          name='isHazard'
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Switch {...field} checked={field.value} />} label='Hazardous' />
          )}
        />
      </Box>
      <MultipleSelectWithSelectAll name='applicableTo' label='Applicable To' options={applicableToOptions} />
      <MultipleSelectWithSelectAll
        name='transportationMode'
        label='Transportation Mode'
        options={transportationModeOptions}
      />

      <Controller
        name='includeWith'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box className='mt-6' data-testid='instructions-form-include-width'>
            <label htmlFor='includeWith' className={FIELD_LABEL_CLASSES}>
              Include With<span className='text-red-600'>*</span>
            </label>
            <MultiSelect
              name={field.name}
              value={field.value?.map(String) ?? []}
              onChange={field.onChange}
              options={includedWithOptions}
              error={!!error}
              MenuProps={{
                MenuListProps: {
                  className: "[&.Mui-focused]:outline-blue-200",
                },
              }}
            >
              {includedWithOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={field.value?.map(String).includes(value)} />
                  {label}
                </MenuItem>
              ))}
            </MultiSelect>
            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </Box>
        )}
      />
    </form>
  )
}

export default InstructionForm
